<template>
  <div id="account-page" class="viewport viewport-with-top-nav viewport-compact">

    <TopNav></TopNav>
    <pull-refresh :on-refresh="onRefresh">
      <div class="display-1 my-3">{{ $t('AccountPage.title') }}</div>
      <div v-if="!profile" class="pt-4 mt-2">
        <div class="card card-shadow card-rounded border-0 mb-4">
          <div class="card-body py-5 text-center">
            <spinner></spinner>
          </div>
        </div>
      </div>
      <template v-if="profile">

        <div class="form-group mb-5">
          <label for="language-select">{{ $t('AccountPage.language') }}</label>
          <select id="language-select" v-model="selectedLanguage" class="custom-select">
            <option v-for="(language, index) in languages" :key="index"
                    :value="language.code">{{ language.name }}
            </option>
          </select>
        </div>
        <div class="card-title mb-3">{{ $t('AccountPage.generalInformation') }}</div>

        <div class="row">
          <div class="col-6 form-group" id="account-page-name">
            <div class="small text-muted">{{ $t('AccountPage.name') }}</div>
            <div>{{ profile.firstName }} {{ profile.lastName }}</div>
          </div>
          <div class="col-6 form-group" id="account-page-gender">
            <div class="small text-muted">{{ $t('AccountPage.gender.title') }}</div>
            <div v-if="profile.gender">{{ $t(`AccountPage.gender.${profile.gender}`) }}</div>
          </div>
        </div>

        <div class="row" v-if="profile.middleName || profile.referenceNumber">
          <div class="col-6 form-group" v-if="profile.middleName" id="account-page-middle-name">
            <div class="small text-muted">{{ $t('AccountPage.middleName') }}</div>
            <div>{{ profile.middleName }}</div>
          </div>
          <div class="col-6 form-group" v-if="profile.referenceNumber" id="account-page-reference-number">
            <div class="small text-muted">{{ $t('AccountPage.customerReferenceNumber') }}</div>
            <div>{{ profile.referenceNumber }}</div>
          </div>
        </div>

        <div class="row">
          <div class="col-6 form-group" id="account-page-pin">
            <div class="small text-muted">{{ $t('AccountPage.pin') }}</div>
            <div>{{ profile.pin }}</div>
          </div>
          <div class="col-6 form-group" id="account-page-birth-date">
            <div class="small text-muted">{{ $t('AccountPage.birthDate') }}</div>
            <div v-if="profile.birthDate">{{ $d(profile.birthDate) }}</div>
          </div>
        </div>

        <div class="row">
          <div class="col-6 form-group" id="account-page-phone-nr">
            <div class="small text-muted">{{ $t('AccountPage.phone') }}</div>
            <div>{{ profile.gsm }}</div>
            <router-link v-if="isPhoneNumberChangeEnabled" :to="{ name: 'update-account'}" id="account-page-change-phone-nr"
                         class="d-inline-flex align-items-center small text-primary page-link">
              <span>{{ $t('AccountPage.updatePhoneNumber') }}</span>
              <Icon icon-name="arrow-ios-forward-outline" class="icon-xs text-primary"></Icon>
            </router-link>
          </div>
          <div class="col-6 form-group">
            <div class="small text-muted">{{ $t('AccountPage.email') }}</div>
            <div>{{ profile.email }}</div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 form-group">
            <div class="small text-muted">{{ $t('AccountPage.address') }}</div>
            <div>
              {{ profile.street }} {{ profile.houseNumber }}
              <span v-if="profile.apartment">-{{ profile.apartment }}</span><br>
              {{ profile.town }} {{ profile.zip }}<br>
            </div>
          </div>
        </div>

        <div v-if="showBiometryLoginOption" class="custom-control custom-switch mb-3 mt-1"
             id="profile-biometry-login">
          <input type="checkbox" class="custom-control-input" v-model="biometricLoginEnabled"
                 id="profile-biometry-login-checkbox">
          <label class="custom-control-label" for="profile-biometry-login-checkbox">{{ biometricLoginLabel }}</label>
        </div>

        <div class="list-group list-group-escape-container mb-5">
          <router-link :to="{ name: 'loans'}" class="list-group-item list-group-item-action list-group-item-with-icon">
            <span>{{ $t('AccountPage.loansHistory') }}</span>
            <Icon icon-name="arrow-ios-forward-outline" icon-class="text-muted-more"></Icon>
          </router-link>
          <button @click="logOut()" class="list-group-item list-group-item-action list-group-item-with-icon">
            <span class="text-danger">{{ $t('AccountPage.logout') }}</span>
            <Icon icon-name="arrow-ios-forward-outline" icon-class="text-muted-more"></Icon>
          </button>
        </div>
      </template>
    </pull-refresh>
    <div class="flex-grow-1"></div><!-- Fill the rest of the page-->
  </div>

</template>

<script>
import store from '@shared/store'
import Icon from '@shared/components/Icon'
import TopNav from '@shared/components/TopNav'
import UserMixin from '@shared/mixins/UserMixin'
import LocaleMixin from '@shared/mixins/LocaleMixin'
import CacheMixin from '@shared/mixins/CacheMixin'
import PullRefresh from '@shared/components/PullRefresh'
import { languagesList as languages } from '@/i18n/languages'
import settings from '@shared/settings'

export default {
  name: 'AccountPage',
  components: { TopNav, Icon, PullRefresh },
  mixins: [UserMixin, LocaleMixin, CacheMixin],
  data() {
    return {
      languages: languages,
      profile: null,
      selectedLanguage: this.getLanguage(),
      biometricLoginEnabled: store.getBiometryUser().pin === store.state.currentUser.pin,
      isPhoneNumberChangeEnabled: settings.featureFlags.isPhoneNumberChangeEnabled() && !settings.isDesktopWeb()
    }
  },
  created() {
    this.$root.$on('biometric-login-disabled', this.onBiometricLoginDisabledEvent)
    this.$root.$on('biometric-login-enabled', this.onBiometricLoginEnabledEvent)
  },
  destroyed() {
    this.$root.$off('biometric-login-disabled', this.onBiometricLoginDisabledEvent)
    this.$root.$off('biometric-login-enabled', this.onBiometricLoginEnabledEvent)
  },
  mounted() {
    this.readProfileFromStore()
    if (this.$route && this.$route.query['biometric-login'] === 'enable') {
      this.biometricLoginEnabled = true
    }
  },
  watch: {
    selectedLanguage(language) {
      this.setLanguage(language)
      this.resetCache().then(() => {
        this.changeLocaleInUserPool(language)
      })
    },
    biometricLoginEnabled(newValue) {
      if (newValue) this.$root.$emit('enable-biometric-login')
      else this.$root.$emit('disable-biometric-login')
    }
  },
  methods: {
    onBiometricLoginDisabledEvent() {
      this.$successToast(this.$t('AccountPage.biometricLoginDisabled'))
      this.biometricLoginEnabled = false
    },
    onBiometricLoginEnabledEvent() {
      this.$successToast(this.$t('AccountPage.biometricLoginEnabled'))
    },
    readProfileFromStore() {
      this.profile = store.state.currentUser
    },
    onRefresh() {
      const pin = this.profile.pin
      this.profile = null
      return this.getAndStoreCustomerProfile(pin).then(() => {
        this.readProfileFromStore()
      })
    }
  },
  computed: {
    showBiometryLoginOption() {
      return settings.isPkiSigningSupported() && settings.featureFlags.isBiometricLoginEnabled()
    },
    biometricLoginLabel() {
      if (settings.isAndroid()) {
        return this.$t('AccountPage.biometricLogin.android')
      } else if (settings.isIOS()) {
        return this.$t('AccountPage.biometricLogin.ios')
      } else {
        return this.$t('AccountPage.biometricLogin.default')
      }
    }
  }
}
</script>
